/** Minimal REST client for the Standard Agents instance. */
import type { ThreadSummary } from "./types.ts";

/** Outcome of an endpoint+token check, with a human-precise failure story. */
export type VerifyResult =
  | { ok: true }
  | { ok: false; reason: string; hint?: string };

/** A parallel-session upgrade quote from the billing service. */
export interface SessionQuote {
  current: number; // sessions in use / currently paid for
  max: number; // hard ceiling
  sessions: number; // the target this quote is for
  amount_cents: number | null; // what Confirm charges now (null = figure unavailable)
  currency: string;
  ends_trial: boolean; // true when confirming ends the $5 trial and converts to paid
  status: string; // subscription status (trialing / active / …)
}

/**
 * Turn a fetch() failure into a precise, human explanation. Node reports the
 * real cause (DNS, refused, timeout, TLS, offline) as error codes buried in
 * the `cause` chain — surface them instead of a generic "didn't work".
 */
function classifyConnectError(err: unknown, endpoint: string): VerifyResult {
  const host = endpoint.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  // Collect codes + messages across the cause chain (undici nests them).
  const codes = new Set<string>();
  const messages: string[] = [];
  for (let e: any = err; e; e = e.cause) {
    if (typeof e.code === "string") codes.add(e.code);
    if (typeof e.message === "string") messages.push(e.message);
    if (Array.isArray(e.errors)) {
      for (const sub of e.errors) {
        if (typeof sub?.code === "string") codes.add(sub.code);
        if (typeof sub?.message === "string") messages.push(sub.message);
      }
    }
  }
  const text = messages.join(" | ");
  const has = (...wanted: string[]) => wanted.some((w) => codes.has(w));

  if (has("ENOTFOUND", "EAI_AGAIN")) {
    return {
      ok: false,
      reason: `Can't resolve ${host} — no such hostname from this machine.`,
      hint: "The endpoint URL is probably wrong (typo, or that server isn't running). Check the URL, your VPN, and local DNS.",
    };
  }
  if (has("ECONNREFUSED")) {
    return {
      ok: false,
      reason: `${host} refused the connection — nothing is listening there.`,
      hint: "The instance looks stopped. Start it (or check the port in the endpoint URL).",
    };
  }
  if (has("EHOSTUNREACH", "ENETUNREACH", "ENETDOWN")) {
    return {
      ok: false,
      reason: `No route to ${host} — the network looks down or unreachable.`,
      hint: "Check your internet connection / VPN.",
    };
  }
  if (has("ETIMEDOUT", "UND_ERR_CONNECT_TIMEOUT", "UND_ERR_HEADERS_TIMEOUT") || (err as any)?.name === "TimeoutError") {
    return {
      ok: false,
      reason: `${host} didn't answer in time.`,
      hint: "The server may be overloaded or a firewall is eating the traffic. Try again.",
    };
  }
  if (
    has(
      "UNABLE_TO_VERIFY_LEAF_SIGNATURE",
      "DEPTH_ZERO_SELF_SIGNED_CERT",
      "SELF_SIGNED_CERT_IN_CHAIN",
      "CERT_HAS_EXPIRED",
      "ERR_TLS_CERT_ALTNAME_INVALID"
    ) || /certificate|TLS|SSL/i.test(text)
  ) {
    return {
      ok: false,
      reason: `TLS problem talking to ${host}: ${text.slice(0, 120)}`,
      hint: "For local/self-signed endpoints, use the http:// URL or trust the local CA.",
    };
  }
  return {
    ok: false,
    reason: `Couldn't reach ${host}${text ? ` (${text.slice(0, 120)})` : ""}.`,
    hint: "This is a connection problem, not a token problem — check the endpoint URL and your network.",
  };
}

/** A subagent child thread with its current lifecycle status. */
export interface SubagentInfo {
  id: string;
  agent_name: string | null;
  title: string | null;
  threadName: string | null;
  status: string; // "running" | "idle" | "terminated" | custom
}

/** An installed Agent Skill's registry metadata. */
export interface SkillInfo {
  name: string;
  description: string;
  version?: string;
  enabled: boolean;
  files: string[];
}

/** A file attachment on an outgoing message (the instance's message-POST shape). */
export interface MessageAttachment {
  name: string;
  mimeType: string;
  data: string; // base64 encoded
  width?: number;
  height?: number;
}

export interface GoalStep {
  step: string;
  status?: string; // "pending" | "in_progress" | "done"
}
export interface GoalData {
  summary: string | null; // short ~5-word label shown while the goal is pursued
  description: string | null;
  steps: GoalStep[];
}

export class ApiClient {
  constructor(
    private endpoint: string,
    private token: string
  ) {}

  get wsEndpoint(): string {
    return this.endpoint.replace(/^http/, "ws");
  }

  /** The instance origin (used to build admin-UI links). */
  get origin(): string {
    return this.endpoint;
  }

  get bearer(): string {
    return this.token;
  }

  private async json<T>(pathname: string, init?: RequestInit): Promise<T> {
    const res = await fetch(`${this.endpoint}${pathname}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
        ...(init?.headers || {}),
      },
    });
    const text = await res.text();
    if (!res.ok) {
      throw new Error(`${init?.method || "GET"} ${pathname} -> ${res.status}: ${text.slice(0, 300)}`);
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  async verify(): Promise<boolean> {
    return (await this.verifyDetailed()).ok;
  }

  /**
   * Check the endpoint + token and say PRECISELY what's wrong when they
   * fail. "That token didn't work" is a lie when the real problem is a typo'd
   * hostname, a stopped server, or a dead network — sign-in errors must let
   * the user tell those apart from an actually-rejected key.
   */
  async verifyDetailed(): Promise<VerifyResult> {
    let res: Response;
    try {
      res = await fetch(`${this.endpoint}/api/auth/me`, {
        headers: { Authorization: `Bearer ${this.token}` },
        signal: AbortSignal.timeout(15_000),
      });
    } catch (err) {
      return classifyConnectError(err, this.endpoint);
    }
    if (res.ok) return { ok: true };
    if (res.status === 401) {
      return {
        ok: false,
        reason: "The instance rejected this token — it's invalid, expired, or was revoked.",
        hint: "Create a fresh API key in the instance settings (or press Enter to sign in with your browser). Keys only work on the instance that minted them.",
      };
    }
    if (res.status === 403) {
      return {
        ok: false,
        reason: "The token was recognized, but this account isn't allowed to use the API here.",
        hint: "Ask an instance admin to grant access, or use a different account's key.",
      };
    }
    if (res.status === 404 || res.status === 405) {
      return {
        ok: false,
        reason: `${this.endpoint} answered, but it doesn't look like a Standard Agents instance (no /api/auth/me).`,
        hint: "Double-check the endpoint URL — this may be a different service.",
      };
    }
    if (res.status >= 500) {
      return {
        ok: false,
        reason: `The instance hit an error while checking the token (HTTP ${res.status}).`,
        hint: "That's a server-side problem — check the instance's logs and try again.",
      };
    }
    return { ok: false, reason: `The instance answered HTTP ${res.status} while checking the token.` };
  }

  /** Agent name → display title map (best effort; for labeling subagents). */
  async listAgents(): Promise<{ name: string; title: string }[]> {
    const res = await this.json<{ agents?: any[] } | any[]>("/api/agents");
    const arr = Array.isArray(res) ? res : res.agents || [];
    return arr
      .filter((a) => a && typeof a.name === "string")
      .map((a) => ({ name: a.name, title: typeof a.title === "string" ? a.title : a.name }));
  }

  async createThread(agentId: string, tags: string[]): Promise<string> {
    const res = await this.json<{ threadId?: string; id?: string }>("/api/threads", {
      method: "POST",
      body: JSON.stringify({ agent_id: agentId, tags }),
    });
    const id = res.threadId || res.id;
    if (!id) throw new Error("Thread create returned no id");
    return id;
  }

  /**
   * List threads for an agent (or several variants of it), optionally
   * filtering to those carrying all given tags. Multiple ids exist because
   * the instance's model gearbox hands a session between agent variants — a
   * thread that last ran in another gear must still show up for resume.
   */
  async listThreads(agentId: string | string[], requireTags: string[]): Promise<ThreadSummary[]> {
    const ids = Array.isArray(agentId) ? agentId : [agentId];
    const pages = await Promise.all(
      ids.map((id) =>
        this.json<{ threads?: any[] } | any[]>(
          `/api/threads?agent_id=${encodeURIComponent(id)}&limit=100`
        ).catch(() => [] as any[])
      )
    );
    const arr = pages.flatMap((res) => (Array.isArray(res) ? res : (res as { threads?: any[] }).threads || []));
    return arr
      .map((t) => ({
        id: t.id,
        tags: Array.isArray(t.tags) ? t.tags : [],
        created_at: t.created_at,
        title: t.title,
        preview: t.preview || t.last_message,
      }))
      .filter((t: ThreadSummary) => requireTags.every((tag) => t.tags.includes(tag)));
  }

  /**
   * Subagent child threads of a thread, each with its current lifecycle status
   * (`running`/`idle`/`terminated`). Sourced from the parent's child registry,
   * which the live `/api/events` stream doesn't carry — so the CLI reads this to
   * learn which subagents are *actually running* (vs idle or finished), both on
   * (re)connect and whenever a subagent thread changes.
   */
  async listSubagents(threadId: string): Promise<SubagentInfo[]> {
    const res = await this.json<{ subagents?: any[] } | any[]>(
      `/api/threads/${threadId}/subagents`
    );
    const arr = Array.isArray(res) ? res : res.subagents || [];
    return arr
      .filter((s) => s && (s.id ?? s.reference))
      .map((s) => ({
        id: s.id ?? s.reference,
        agent_name: s.agent_name ?? s.name ?? null,
        title: s.title ?? null,
        threadName: s.threadName ?? s.thread_name ?? null,
        status: typeof s.status === "string" ? s.status : "running",
      }));
  }

  /**
   * Send a user message, optionally with attachments (e.g. pasted images).
   * Attachments use the instance's message-POST shape — base64 `data` +
   * `mimeType` — which the server stores in the thread filesystem and injects
   * into the LLM's vision context as real image content blocks.
   */
  async sendMessage(threadId: string, content: string, attachments?: MessageAttachment[]): Promise<void> {
    const body: Record<string, unknown> = { role: "user", content };
    if (attachments && attachments.length > 0) body.attachments = attachments;
    await this.json(`/api/threads/${threadId}/messages`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async getMessages(threadId: string, limit = 50, order?: "asc" | "desc"): Promise<any[]> {
    const orderParam = order ? `&order=${order}` : "";
    const res = await this.json<{ messages?: any[] } | any[]>(
      `/api/threads/${threadId}/messages?limit=${limit}${orderParam}`
    );
    return Array.isArray(res) ? res : res.messages || [];
  }

  async getLogs(threadId: string, limit = 100): Promise<any[]> {
    const res = await this.json<{ logs?: any[] } | any[]>(
      `/api/threads/${threadId}/logs?limit=${limit}&order=desc`
    );
    return Array.isArray(res) ? res : res.logs || [];
  }

  /**
   * Deliver a durable forwarded tool result to the thread, resuming the turn.
   * Retries with backoff — this is the durable delivery path, so it must land
   * even if the connection is briefly flaky after a permission wait.
   */
  async postToolResult(
    threadId: string,
    toolCallId: string,
    ok: boolean,
    result?: string,
    error?: string
  ): Promise<boolean> {
    const body = JSON.stringify({ tool_call_id: toolCallId, ok, result, error });
    for (let attempt = 0; attempt < 6; attempt++) {
      try {
        await this.json(`/api/threads/${threadId}/tool-result`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
        });
        return true;
      } catch {
        await new Promise((r) => setTimeout(r, Math.min(500 * 2 ** attempt, 8000)));
      }
    }
    return false;
  }

  /**
   * Download raw file bytes from the thread's filesystem (e.g. an
   * /attachments/* asset a subagent generated). Returns null on any failure.
   */
  async fetchFile(threadId: string, fsPath: string): Promise<Buffer | null> {
    const clean = fsPath.startsWith("/") ? fsPath : `/${fsPath}`;
    try {
      const res = await fetch(`${this.endpoint}/api/threads/${threadId}/fs${clean}`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!res.ok) return null;
      return Buffer.from(await res.arrayBuffer());
    } catch {
      return null;
    }
  }

  /** Read a value from the thread's durable KV store (null if absent). */
  async kvGet(threadId: string, key: string): Promise<unknown> {
    try {
      const res = await this.json<{ value?: unknown }>(
        `/api/threads/${threadId}/kv?key=${encodeURIComponent(key)}`
      );
      return res?.value ?? null;
    } catch {
      return null; // 404 = key not yet set
    }
  }

  /** Write a value to the thread's durable KV store. */
  async kvSet(threadId: string, key: string, value: unknown): Promise<void> {
    try {
      await this.json(`/api/threads/${threadId}/kv`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, value }),
      });
    } catch {
      // best effort
    }
  }

  /** Mint a pre-authed standardcode.ai account-dashboard URL for this thread's
   *  user. Returns null when the chain is unavailable — the caller falls back
   *  to the plain dashboard URL (sign-in by inbox link). */
  async accountLink(threadId: string): Promise<{ url: string; preauthed: boolean } | null> {
    try {
      const res = await this.json<{ url?: string; preauthed?: boolean }>(
        `/api/threads/${threadId}/account_link`,
        { method: "POST" }
      );
      return res?.url ? { url: res.url, preauthed: res.preauthed === true } : null;
    } catch {
      return null;
    }
  }

  /** Preview the cost of changing to `sessions` parallel sessions (default +1).
   *  Returns null when billing is unavailable / there's no subscription. */
  async sessionsQuote(threadId: string, sessions?: number): Promise<SessionQuote | null> {
    try {
      return await this.json<SessionQuote>(`/api/threads/${threadId}/sessions_quote`, {
        method: "POST",
        body: JSON.stringify(typeof sessions === "number" ? { sessions } : {}),
      });
    } catch {
      return null;
    }
  }

  /** Apply a parallel-session change (charges via Stripe). Throws on failure. */
  async sessionsUpgrade(threadId: string, sessions: number): Promise<{ ok?: boolean; sessions?: number; error?: string }> {
    return this.json(`/api/threads/${threadId}/sessions_upgrade`, {
      method: "POST",
      body: JSON.stringify({ sessions }),
    });
  }

  // ── skills (instance-global Agent Skills library) ─────────────────────────

  /** Installed skills — metadata only. Includes disabled skills. */
  async listSkills(): Promise<SkillInfo[]> {
    const res = await this.json<{ skills?: SkillInfo[] }>(`/api/skills?all=1`);
    return Array.isArray(res?.skills) ? res.skills : [];
  }

  /** Enable/disable an installed skill. */
  async setSkillEnabled(name: string, enabled: boolean): Promise<void> {
    await this.json(`/api/skills/${encodeURIComponent(name)}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    });
  }

  /** Uninstall a skill. */
  async removeSkill(name: string): Promise<void> {
    await this.json(`/api/skills/${encodeURIComponent(name)}`, { method: "DELETE" });
  }

  /**
   * Kick off background conversation compaction now. Spawns the compaction
   * subagent directly server-side (so it fires even while the thread is idle).
   */
  async compact(threadId: string): Promise<void> {
    await this.json(`/api/threads/${threadId}/compact`, { method: "POST" });
  }

  async stop(threadId: string): Promise<void> {
    try {
      await this.json(`/api/threads/${threadId}/stop`, { method: "POST" });
    } catch {
      // best effort
    }
  }

  /** The thread's current goal (set via set_goal / update_goal_step). */
  async getGoal(threadId: string): Promise<GoalData> {
    try {
      const r = await this.json<Partial<GoalData>>(`/api/threads/${threadId}/goal`);
      return {
        summary: r?.summary ?? null,
        description: r?.description ?? null,
        steps: Array.isArray(r?.steps) ? r.steps : [],
      };
    } catch {
      return { summary: null, description: null, steps: [] };
    }
  }
}
